import { type Material, type Matrix4, Object3D, type Scene } from 'three';
import { formatNodeId, type NodeId, markNodeId } from '@/lib/threeScene/nodeId';
import { formatNode, type ObjectNode } from '@/lib/threeScene/nodes/object';
import { getNode, type NodeMap, calculateWorldTransform } from '@/lib/threeScene/nodeMap';
import { useDeveloperSettings } from '@/stores/developerSettings';
import { formatMaterialId, markMaterialId, type MaterialId } from '@/lib/threeScene/materials/materialId';

export type SceneState<Node extends ObjectNode> = {
    readonly nodeMap: NodeMap<Node>;
};

export function sceneState<Node extends ObjectNode>(): SceneState<Node> {
    return {
        nodeMap: new Map<NodeId, Node>(),
    };
}

export class SceneContext<
    Node extends ObjectNode = ObjectNode,
    State extends SceneState<Node> = SceneState<Node>,
> {
    constructor(
        public readonly scene: Scene,
        public readonly state: State,
    ) {}

    /** A map of which nodes map to which objects */
    readonly objectMap: Map<NodeId, Object3D> = new Map();
    readonly materialMap: Map<MaterialId, Material> = new Map();

    get nodes(): NodeMap<Node> {
        return this.state.nodeMap;
    }

    tryGetNode(id: NodeId): Node | undefined {
        return this.state.nodeMap.get(id);
    }

    getNode(id: NodeId): Node {
        return getNode(this.state.nodeMap, id);
    }

    worldTransform(id: NodeId): Matrix4 {
        return calculateWorldTransform(this.state.nodeMap, id);
    }

    addNode(node: Node, object: Object3D) {
        // Check the node does not already exist in the scene
        if (this.tryGetNode(node.id)) {
            if (!useDeveloperSettings().suppressReloadErrors) {
                throw new Error(`${formatNode(node)} already exists in scene`);
            }
        }

        this.state.nodeMap.set(node.id, node);
        this.objectMap.set(node.id, object);
        markNodeId(object, node.id);

        return node;
    }

    removeNode(id: NodeId): void {
        // Check the node does not already exist in the scene
        const node = this.tryGetNode(id);
        if (!node) {
            throw new Error(`Cannot find node ${formatNodeId(id)} to remove in scene`);
        }

        // Reparent child nodes
        this.state.nodeMap.forEach((otherNode: ObjectNode) => {
            if (otherNode.parent === id) {
                otherNode.parent = node.parent;
            }
        });
        this.state.nodeMap.delete(id);
        this.objectMap.delete(id);
    }

    getObject(id: NodeId): Object3D {
        const result = this.objectMap.get(id);
        if (result !== undefined) {
            return result;
        } else {
            throw Error(`Could not get Object3D for node ${formatNodeId(id)}`);
        }
    }

    getParentObject(node: ObjectNode): Object3D {
        return node.parent === null ? this.scene : this.getObject(node.parent);
    }

    tryGetMaterial(id: MaterialId): Material | undefined {
        return this.materialMap.get(id);
    }

    getMaterial(id: MaterialId): Material {
        const result = this.materialMap.get(id);
        if (result !== undefined) {
            return result;
        } else {
            throw Error(`Could not get Material ${formatMaterialId}`);
        }
    }

    addMaterial(id: MaterialId, material: Material): void {
        // Check the node does not already exist in the scene
        if (this.tryGetMaterial(id)) {
            if (!useDeveloperSettings().suppressReloadErrors) {
                throw new Error(`Material with id ${material} already exists in scene`);
            }
        }
        markMaterialId(material, id);
        this.materialMap.set(id, material);
    }
}
